import type { ChangeBookingFlow } from '@/features/manage/manageActions';
import type {
  SearchJourneyOfferDTO,
  SearchJourneyOffersDTO,
} from '@/types/dto/agent';
import type { OfferMapByLegId } from '@/types/offer';
import { isNonEmptyString } from '@/utils/typeGuards';

export const getOfferAvailablePlaces = (offer: SearchJourneyOfferDTO) => {
  // Availability is reported per place property combination, in
  // reservationLegCoverage and/or reservationOfferParts[].availablePlaces
  // depending on the connector. The row carrying no place properties is not
  // authoritative - it can read 0 while property-specific rows still have
  // places. Mirror the agent tool and take the largest of every row.
  const availabilities = [
    ...(offer.reservationLegCoverage ?? []),
    ...(offer.reservationOfferParts ?? []).flatMap(
      ({ availablePlaces }) => availablePlaces ?? []
    ),
  ]
    .map(({ numericAvailability }) => numericAvailability)
    .filter((numericAvailability) => numericAvailability !== undefined);

  if (availabilities.length) {
    return Math.max(...availabilities);
  }

  return offer.availablePlaces;
};

export const isOfferSelectable = (offer: SearchJourneyOfferDTO) =>
  Boolean(
    offer.id &&
      offer.price &&
      (getOfferAvailablePlaces(offer) === undefined ||
        getOfferAvailablePlaces(offer)! > 0)
  );

export const isMultiLegOffer = (offer: SearchJourneyOfferDTO) =>
  (offer.coveredLegIds?.length ?? 0) > 1;

export const getJourneyLegIds = (journey: SearchJourneyOffersDTO) =>
  journey?.trips?.reduce<Array<string>>((acc, { legs }) => {
    acc.push(...(legs ?? []).map((leg) => leg.id).filter(isNonEmptyString));

    return acc;
  }, []) ?? [];

export const getJourneyNonWalkingLegIds = (journey: SearchJourneyOffersDTO) => {
  return (
    journey?.trips?.reduce<Array<string>>((acc, { legs }) => {
      acc.push(
        ...(legs ?? [])
          .filter((leg) => leg.ptMode?.code?.toLocaleUpperCase() !== 'WALK')
          .map((leg) => leg.id)
          .filter(isNonEmptyString)
      );
      return acc;
    }, []) ?? []
  );
};

export const getFirstCoveredLegId = (
  offer: SearchJourneyOfferDTO,
  legIds: string[]
) => legIds.find((id) => offer.coveredLegIds?.includes(id));

export const shouldDisplayOfferOnLeg = (
  offer: SearchJourneyOfferDTO,
  legId: string,
  legIds: string[]
) => !isMultiLegOffer(offer) || getFirstCoveredLegId(offer, legIds) === legId;

export type InventoryClass = SearchJourneyOfferDTO['offerAccommodationType'];

// both tab rows are displayed in this exact order (BR-57662)
const INVENTORY_CLASS_ORDER: Record<InventoryClass, number> = {
  SEAT: 0,
  SEAT_COMPARTMENT: 1,
  BED: 2,
  COMPARTMENT: 3,
};

// the schema types travelClass as a plain string, so the known classes are
// listed here
const TRAVEL_CLASS_ORDER = ['FOURTH', 'THIRD', 'SECOND', 'FIRST'] as const;

export type TravelClass = (typeof TRAVEL_CLASS_ORDER)[number];

const getAvailableInventoryClasses = (
  offers: SearchJourneyOfferDTO[]
): ReadonlyArray<InventoryClass> =>
  [
    ...new Set(
      offers.map(({ offerAccommodationType }) => offerAccommodationType)
    ),
  ].sort((a, b) => INVENTORY_CLASS_ORDER[a] - INVENTORY_CLASS_ORDER[b]);

const getAvailableTravelClasses = (
  offers: SearchJourneyOfferDTO[]
): ReadonlyArray<TravelClass> => {
  const available = new Set(offers.map(({ travelClass }) => travelClass));

  return TRAVEL_CLASS_ORDER.filter((travelClass) => available.has(travelClass));
};

const resolveClassSelection = <T>(
  available: ReadonlyArray<T>,
  selected?: T,
  preferred?: T
) => {
  if (selected && available.includes(selected)) {
    return selected;
  }

  if (preferred && available.includes(preferred)) {
    return preferred;
  }

  return available[0];
};

export interface LegClassView {
  inventoryClasses: ReadonlyArray<InventoryClass>;
  inventoryClass?: InventoryClass;
  travelClasses: ReadonlyArray<TravelClass>;
  travelClass?: TravelClass;
  visibleOffers: SearchJourneyOfferDTO[];
}

// resolves the classes alongside the offers they filter, so the highlighted tab
// and the offers below it cannot disagree
export const getLegClassView = (
  offers: SearchJourneyOfferDTO[],
  selection: {
    inventoryClass?: InventoryClass;
    travelClass?: TravelClass;
    preselectedOffer?: SearchJourneyOfferDTO;
  }
): LegClassView => {
  const inventoryClasses = getAvailableInventoryClasses(offers);
  const inventoryClass = resolveClassSelection(
    inventoryClasses,
    selection.inventoryClass,
    selection.preselectedOffer?.offerAccommodationType
  );
  const inventoryClassOffers = inventoryClass
    ? offers.filter((offer) => offer.offerAccommodationType === inventoryClass)
    : offers;

  const travelClasses = getAvailableTravelClasses(inventoryClassOffers);
  const travelClass = resolveClassSelection(
    travelClasses,
    selection.travelClass,
    travelClasses.find(
      (available) => available === selection.preselectedOffer?.travelClass
    )
  );
  const visibleOffers = travelClass
    ? inventoryClassOffers.filter((offer) => offer.travelClass === travelClass)
    : inventoryClassOffers;

  return {
    inventoryClasses,
    inventoryClass,
    travelClasses,
    travelClass,
    visibleOffers,
  };
};

const getCoveredLegIdsInJourney = (
  offer: SearchJourneyOfferDTO,
  legIds: string[],
  fallbackLegId: string
) => {
  if (!isMultiLegOffer(offer)) {
    return [fallbackLegId];
  }

  const coveredLegIds =
    offer.coveredLegIds?.filter((id) => legIds.includes(id)) ?? [];

  return coveredLegIds.length > 0 ? coveredLegIds : [fallbackLegId];
};

const clearOfferCoverage = (
  map: OfferMapByLegId,
  offer: SearchJourneyOfferDTO,
  legIds: string[],
  fallbackLegId: string
) => {
  getCoveredLegIdsInJourney(offer, legIds, fallbackLegId).forEach((id) => {
    if (map[id]?.id === offer.id) {
      delete map[id];
    }
  });
};

const offerConflictsWithMap = (
  offer: SearchJourneyOfferDTO,
  map: OfferMapByLegId,
  legIds: string[],
  fallbackLegId: string
) =>
  getCoveredLegIdsInJourney(offer, legIds, fallbackLegId).some(
    (id) => map[id]?.id && map[id]?.id !== offer.id
  );

export const applyOfferSelection = (
  currentMap: OfferMapByLegId,
  legId: string,
  offer: SearchJourneyOfferDTO,
  legIds: string[]
): OfferMapByLegId => {
  const newMap = { ...currentMap };
  const existingOffer = newMap[legId];

  if (existingOffer) {
    clearOfferCoverage(newMap, existingOffer, legIds, legId);
  }

  const legsToSet = getCoveredLegIdsInJourney(offer, legIds, legId);

  legsToSet.forEach((id) => {
    const conflicting = newMap[id];
    if (conflicting?.id && conflicting.id !== offer.id) {
      clearOfferCoverage(newMap, conflicting, legIds, id);
    }
  });

  legsToSet.forEach((id) => {
    newMap[id] = offer;
  });

  return newMap;
};

const canFillRemainingLegs = (
  map: OfferMapByLegId,
  legIds: string[],
  offers: SearchJourneyOfferDTO[]
): boolean => {
  const uncoveredLegId = legIds.find((id) => !map[id]?.id);
  if (!uncoveredLegId) {
    return true;
  }

  return offers.some((offer) => {
    if (
      !offer.coveredLegIds?.includes(uncoveredLegId) ||
      !isOfferSelectable(offer) ||
      offerConflictsWithMap(offer, map, legIds, uncoveredLegId)
    ) {
      return false;
    }

    return canFillRemainingLegs(
      applyOfferSelection(map, uncoveredLegId, offer, legIds),
      legIds,
      offers
    );
  });
};

const canCompleteJourneyWithOffer = (
  map: OfferMapByLegId,
  legId: string,
  offer: SearchJourneyOfferDTO,
  legIds: string[],
  offers: SearchJourneyOfferDTO[]
) =>
  canFillRemainingLegs(
    applyOfferSelection(map, legId, offer, legIds),
    legIds,
    offers
  );

export const getUniqueSelectedOffers = (
  offers: Array<SearchJourneyOfferDTO | undefined>
) => {
  const seen = new Set<string>();

  return offers.filter((offer): offer is SearchJourneyOfferDTO => {
    if (!offer?.id || seen.has(offer.id)) {
      return false;
    }

    seen.add(offer.id);
    return true;
  });
};

export const getDefaultOfferMap = (journey: SearchJourneyOffersDTO) => {
  const legIds = getJourneyNonWalkingLegIds(journey);
  const offers = journey?.trips?.flatMap((trip) => trip.offers ?? []) ?? [];

  return legIds.reduce<OfferMapByLegId>((map, legId) => {
    if (map[legId]?.id) {
      return map;
    }

    const cheapestMatchingOffer = offers.reduce<SearchJourneyOfferDTO | null>(
      (cheapestOffer, currentOffer) => {
        if (
          !currentOffer.coveredLegIds?.includes(legId) ||
          !isOfferSelectable(currentOffer) ||
          offerConflictsWithMap(currentOffer, map, legIds, legId) ||
          !canCompleteJourneyWithOffer(map, legId, currentOffer, legIds, offers)
        ) {
          return cheapestOffer;
        }

        return !cheapestOffer ||
          (currentOffer.price?.amount ?? 0) < (cheapestOffer.price?.amount ?? 0)
          ? currentOffer
          : cheapestOffer;
      },
      null
    );

    if (!cheapestMatchingOffer) {
      return map;
    }

    return applyOfferSelection(map, legId, cheapestMatchingOffer, legIds);
  }, {});
};

export const purchaseFlowJourneyOrOfferPaths = [
  '/purchase/outbound/journey',
  '/purchase/outbound/offer',
  '/purchase/inbound/journey',
  '/purchase/inbound/offer',
  '/purchase/non-trip/offer',
];

export const shouldShowPurchaseSearchWidget = (
  pathname: string,
  changeFlow: ChangeBookingFlow | null
) => {
  if (changeFlow === 'changeTravelClass') {
    return false;
  }
  return ['/purchase', ...purchaseFlowJourneyOrOfferPaths].includes(pathname);
};
